import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, Copy, CreditCard, Landmark, Smartphone, Upload } from "lucide-react";
import { api, API, money } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

export const PlanPicker = ({ plans, value, onChange }) => (
  <div className="plan-grid" data-testid="plan-picker">
    {plans.map(p => (
      <button type="button" key={p.key} className={value === p.key ? "plan-card active" : "plan-card"} onClick={() => onChange(p.key)} data-testid={`plan-${p.key}`}>
        <span className="card-kicker">{p.duration_months ? `${p.duration_months} months` : "Lifetime"}</span>
        <strong>{money(p.amount, p.currency)}</strong>
        <b>{p.label}</b>
        <p>{p.description}</p>
        {value === p.key && <i><Check size={13} /></i>}
      </button>
    ))}
  </div>
);

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const sc = document.createElement("script"); sc.src = "https://checkout.razorpay.com/v1/checkout.js"; sc.onload = () => resolve(true); sc.onerror = () => resolve(false); document.body.appendChild(sc);
});

export const PaymentStep = ({ plan, instructions, onDone }) => {
  const [config, setConfig] = useState(null);
  const [online, setOnline] = useState("");
  useEffect(() => { api("/public/payment-config").then(setConfig).catch(() => setConfig({ razorpay_enabled: false })); }, []);
  const payOnline = async () => {
    setOnline("Starting secure checkout…"); setErr("");
    try {
      if (!(await loadRazorpay())) throw new Error("Could not load Razorpay checkout. Check your connection.");
      const o = await api("/payments/razorpay/order", { method: "POST", body: JSON.stringify({ plan_key: plan.key }) });
      const rz = new window.Razorpay({
        key: o.key_id, amount: o.amount, currency: o.currency, order_id: o.order_id, name: "DCEA Hubli–Dharwad", description: o.description, prefill: o.prefill, theme: { color: "#D4AF37" },
        handler: async resp => {
          try { const v = await api("/payments/razorpay/verify", { method: "POST", body: JSON.stringify(resp) }); setOnline(v.message); onDone({ ...v, online: true }); }
          catch (ex) { setErr(ex.message); setOnline(""); }
        },
        modal: { ondismiss: () => setOnline("") }
      });
      rz.on("payment.failed", r => { setErr(r.error?.description || "Payment failed. You can retry or use bank transfer."); setOnline(""); });
      rz.open();
    } catch (ex) { setErr(ex.message); setOnline(""); }
  };
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const fileRef = useRef();

  const copy = (k, v) => { navigator.clipboard?.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };

  const upload = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/account/upload-proof`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setProof(data.url);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await api("/payments/manual", { method: "POST", body: JSON.stringify({ plan_key: plan.key, reference, proof_url: proof }) });
      onDone(res);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const rows = [["UPI ID", instructions.upi_id, Smartphone], ["Account name", instructions.account_name, Landmark], ["Bank", instructions.bank_name], ["Account number", instructions.account_number], ["IFSC", instructions.ifsc]].filter(r => r[1]);

  return (
    <form className="application-form" onSubmit={submit} data-testid="payment-step">
      <h2>Pay {money(plan.amount, plan.currency)} · {plan.label}</h2>
      {config?.razorpay_enabled && (
        <div className="pay-online full" data-testid="razorpay-section">
          <div><b>Pay instantly online</b><span>UPI · Cards · Netbanking · Wallets — membership activates automatically.</span></div>
          <button type="button" className="button button-gold" onClick={payOnline} disabled={!!online} data-testid="razorpay-pay-button"><CreditCard size={15} /> {online || `Pay ${money(plan.amount, plan.currency)} now`}</button>
        </div>
      )}
      {config?.razorpay_enabled && <div className="auth-divider full"><span>or pay by bank transfer / UPI manually</span></div>}
      <div className="pay-box full">
        {rows.map(([label, value]) => (
          <div className="pay-row" key={label}>
            <span>{label}</span>
            <b data-testid={`pay-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</b>
            <button type="button" onClick={() => copy(label, value)} aria-label={`Copy ${label}`}>{copied === label ? <Check size={13} /> : <Copy size={13} />}</button>
          </div>
        ))}
        {instructions.note && <p className="pay-note">{instructions.note}</p>}
      </div>
      <label className="full">Transaction / UTR reference
        <input required minLength={4} value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g. 4152XXXXXXXX" data-testid="payment-reference-input" />
      </label>
      <label className="full">Payment screenshot or receipt (optional)
        <div className="media-field-row">
          <input value={proof} readOnly placeholder="No file uploaded" data-testid="payment-proof-url" />
          <button type="button" className="button button-ghost dark" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="payment-proof-upload"><Upload size={14} /> Upload</button>
          <input ref={fileRef} type="file" hidden accept="image/*,application/pdf" onChange={upload} data-testid="payment-proof-file" />
        </div>
      </label>
      {err && <div className="error-message" data-testid="payment-error">{err}</div>}
      <button className="button button-gold" disabled={busy} data-testid="payment-submit-button">
        {busy ? "Submitting…" : "I have paid — submit for verification"} <ArrowRight size={15} />
      </button>
    </form>
  );
};

const FIELDS = [["name", "Full name", true], ["email", "Email address", true], ["phone", "Phone number", true], ["password", "Create password (min 8 characters)", true], ["qualification", "Qualification", true], ["institution", "Diploma institution (optional)", false], ["passing_year", "Year of passing (optional)", false], ["organization", "Organization (optional)", false], ["city", "City (optional)", false]];

export default function RegisterPage() {
  const [plans, setPlans] = useState([]);
  const [instructions, setInstructions] = useState({});
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(params.get("plan") || "annual");
  const [form, setForm] = useState({ qualification: "Diploma Civil Engineering" });
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api("/public/membership-plans").then(d => { setPlans(d.plans || []); setInstructions(d.payment_instructions || {}); }).catch(() => {});
  }, []);

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await api("/auth/register", { method: "POST", body: JSON.stringify({ ...form, plan_key: plan }) });
      setUser(u);
      setStep(2);
      window.scrollTo(0, 0);
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const selected = plans.find(p => p.key === plan) || plans[0];

  return (
    <main className="inner-page register-page">
      <div className="container form-layout">
        <div className="page-intro">
          <Link to="/" className="brand" data-testid="register-brand"><span className="brand-mark">D</span><span><b>DCEA Hubli–Dharwad</b><small>Member registration</small></span></Link>
          <span className="eyebrow">Step {step} of 2</span>
          <h1>{step === 1 ? <>Join <em className="gold-text">DCEA.</em></> : <>Complete your <em className="gold-text">payment.</em></>}</h1>
          <p>{step === 1 ? "Create your account and choose a membership plan. Payment follows on the next step." : "Transfer the fee using the details shown and submit your transaction reference. Your membership activates once the treasurer verifies it."}</p>
          <div className="steps" data-testid="register-steps">
            <span className={step >= 1 ? "done" : ""}><i>1</i> Account & plan</span>
            <span className={step >= 2 ? "done" : ""}><i>2</i> Payment</span>
            <span><i>3</i> Verification</span>
          </div>
          <p className="auth-foot">Already a member? <Link to="/login" data-testid="register-login-link">Sign in</Link></p>
        </div>
        {step === 1 ? (
          <form className="application-form" onSubmit={submit} data-testid="register-form">
            <h2>Choose your plan</h2>
            <div className="full">{plans.length ? <PlanPicker plans={plans} value={plan} onChange={setPlan} /> : <div className="hint">Loading plans…</div>}</div>
            <h2 style={{ marginTop: 10 }}>Your details</h2>
            {FIELDS.map(([k, l, req]) => (
              <label key={k}>{l}
                <input required={req} type={k === "password" ? "password" : k === "email" ? "email" : "text"} minLength={k === "password" ? 8 : undefined} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`register-${k}-input`} />
              </label>
            ))}
            {err && <div className="error-message" data-testid="register-error">{err}</div>}
            <button className="button button-gold" disabled={busy} data-testid="register-submit-button">
              {busy ? "Creating account…" : `Continue to payment${selected ? ` · ${money(selected.amount, selected.currency)}` : ""}`} <ArrowRight size={15} />
            </button>
          </form>
        ) : step === 2 && selected ? (
          <PaymentStep plan={selected} instructions={instructions} onDone={() => setStep(3)} />
        ) : (
          <div className="application-form" data-testid="register-complete">
            <div className="success-message">
              <CheckCircle2 size={24} style={{ marginBottom: 10 }} />
              <strong>Payment submitted for verification.</strong>
              <div>We'll activate your membership and issue your Member ID as soon as the treasurer confirms the transfer. Track the status from your dashboard.</div>
            </div>
            <button className="button button-gold" onClick={() => nav("/account")} data-testid="register-go-dashboard">Open my dashboard <ArrowRight size={15} /></button>
          </div>
        )}
      </div>
    </main>
  );
}
